/**
 * Studio → Functions — thin route shim (Wave 3 Stream 1 follow-up).
 *
 * Backstory: PRs #57 + #58 shipped the tenant_functions schema, the
 * isolated-vm `/test` sandbox, and a Monaco-backed list at
 * /connections/functions. PR #377 added the sysadmin-side wrapper-function
 * wizard backed by the same scope-aware `ToolDeveloperPage` shell. The
 * TabBar slot in Studio + a `/studio/functions` route were never added,
 * so click-through reverify caught Studio rendering only Models | Tools |
 * Skills | Prompts and `/studio/functions` returning 404.
 *
 * This file is intentionally minimal — it mounts the already-exported
 * tenant `FunctionsPage` (the same component `/connections/functions`
 * renders) so that:
 *
 *   1. Studio gets a working Functions list immediately, with full
 *      lifecycle controls (deploy / disable / delete / edit-in-Monaco).
 *   2. Behaviour stays in lockstep with the connections-side surface —
 *      one component, two mount points (until we move authoring fully
 *      to Studio and retire the connections rail in a future pass).
 *   3. The "New Function" affordance the page renders deep-links to
 *      `/connections/new-function`, which already uses the scope-aware
 *      `<ToolDeveloperPage scope="tenant" />` (per AL-B4 / PR #377), so
 *      no duplicate builder is needed.
 *
 * Permission gating: the underlying API (tenant_functions CRUD) is
 * gated on `write:argus.functions` for mutations and `read:argus.functions`
 * for reads — both already declared in
 * `@argusai/shared` `permissions.ts`. The route itself is reachable to
 * anyone who can see the Studio rail; per-action gating is enforced
 * server-side, identical to the connections-side route.
 */

import { createFileRoute } from '@tanstack/react-router';
import { FunctionsPage } from '@/routes/connections/functions';

export const Route = createFileRoute('/studio/functions')({
  component: StudioFunctionsRoute,
});

function StudioFunctionsRoute() {
  // 2026-05-12 browser audit P0 — FunctionsPage hands off to <Outlet/>
  // when the URL is not the index URL. Without explicit indexPath, the
  // default '/connections/functions' did not match '/studio/functions',
  // so this mount rendered an empty Outlet (the Studio rail has no
  // child routes under /studio/functions). Pass indexPath so the index
  // check fires correctly here.
  return <FunctionsPage indexPath="/studio/functions" />;
}
